import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isTablet } from '@westwing/ui-kit/breakPoints';
import { useCookies } from 'react-cookie';
import { OverlayId } from 'Client/components/Overlays/types';
import { hideOverlay, showOverlay } from 'Client/redux/overlays/actions';
import useShowPromoBarBanner from 'Client/hooks/flags/useShowPromoBarBanner';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';
import useUserTracking from 'Client/hooks/flags/useUserTracking';
import { PageType } from 'Client/hooks/UrlManager/pageTypeHooks';
import { ProductListingPages } from 'Client/hooks/UrlManager/types';
import useRouter from 'Client/hooks/useRouter';
import { getPageTypeByUrl } from 'Client/hooks/UrlManager/pageTypeService';
import useGa from 'Client/hooks/useGa';
import useBellaPush from 'Client/hooks/useBellaHistoryPush';
import { smartAppUrl, Trackers } from 'Common/helpers/app';
import { TrackAppBannerActions } from 'Common/ga/types';
import StateInterface from 'Client/redux/types';
import { getOverlayById } from 'Client/redux/overlays/selectors';
import { useLaunchDarklyTracker } from 'Client/LaunchDarklyTracker';
import useDeviceType from 'Client/hooks/useDeviceType';

const APP_BANNER_DISMISSED = 'p100_app_banner_dismissed';

export const parseAppBannerCoookie = (cookies: {
    [x: string]: any;
}): { nextCookie?: string; currentCookieIndicatesShow: boolean } => {
    const cookie: string = (cookies && cookies[APP_BANNER_DISMISSED]) || '';
    const [viewCountStr, showAgainTimestampStr] = cookie.split(',');
    const viewCount = Number(viewCountStr);
    const showAgainTimestamp = Number(showAgainTimestampStr);
    const currentTimestamp = Date.now();
    const threeDaysInTheFuture = currentTimestamp + 86400000 * 3;

    // first time seeing the banner
    if ([NaN, 0].includes(viewCount) || [NaN, 0].includes(showAgainTimestamp)) {
        return {
            nextCookie: `1,${threeDaysInTheFuture}`,
            currentCookieIndicatesShow: true,
        };
    }

    // second time seeing the banner
    if (viewCount === 1 && showAgainTimestamp < currentTimestamp) {
        return {
            nextCookie: `${viewCount + 1},${threeDaysInTheFuture}`,
            currentCookieIndicatesShow: true,
        };
    }

    // third+ time seeing banner
    if (showAgainTimestamp < currentTimestamp) {
        const thirtyDaysInTheFuture = currentTimestamp + 86400000 * 30;
        return {
            nextCookie: `${viewCount + 1},${thirtyDaysInTheFuture}`,
            currentCookieIndicatesShow: true,
        };
    }

    return {
        currentCookieIndicatesShow: false,
    };
};

export const useShowAppBanner = (id: string) => {
    const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
    const { p100AppBanner } = useFeatureToggle();
    const showClubHeader = useShowPromoBarBanner();
    const { isLoggedIn } = useUserTracking();
    const [cookies] = useCookies([APP_BANNER_DISMISSED]);
    const { currentCookieIndicatesShow } = parseAppBannerCoookie(cookies);

    const {
        location: { pathname },
        host,
    } = useRouter();
    const { pageType: currentPageType } = getPageTypeByUrl(pathname, host);

    const pageWhiteList = [...ProductListingPages, PageType.HOMEPAGE];

    const shouldShow = !!(
        currentCookieIndicatesShow && // hide if shown recently
        pageWhiteList.includes(currentPageType) &&
        p100AppBanner === id && // For A/B/C test, to identify the correct Banner/Overlay
        !showClubHeader && // hide if shop2club banner is displayed
        isUserLoggedIn
    );

    useEffect(() => {
        // We need to do this to make sure the appBanner loads only on client side.
        setIsUserLoggedIn(!!isLoggedIn);
    }, [isLoggedIn]);

    return shouldShow;
};

export const useActionsAppBanner = (id: string, trackers: Trackers) => {
    const { p100AppBanner } = useFeatureToggle();
    const launchDarklyTracker = useLaunchDarklyTracker();
    const [cookies, setCookie] = useCookies([APP_BANNER_DISMISSED]);
    const { nextCookie } = parseAppBannerCoookie(cookies);
    const setDismissed = () => {
        const cookieData = {
            path: '/',
            expires: new Date(Date.now() + 86400000 * 180), // 180 days
        };

        setCookie(APP_BANNER_DISMISSED, nextCookie, cookieData);
    };

    const {
        location: { pathname },
        host,
    } = useRouter();
    const bellaPush = useBellaPush();
    const gaTracking = useGa();
    const dispatch = useDispatch();
    const { pageType: currentPageType } = getPageTypeByUrl(pathname, host);
    const { getByUserAgent } = useDeviceType();

    const isDoiOverlayActive = useSelector<StateInterface, boolean>(
        state => getOverlayById(state.overlays, OverlayId.DOUBLE_OPT_IN_OVERLAY).isActive
    );

    const shouldShow = useShowAppBanner(id);

    const onBackdropDismiss = () => {
        if (p100AppBanner === 'small') {
            dispatch(hideOverlay(OverlayId.APP_PUSH_SMALL));
            gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.SmallPushBannerCloseBackground, currentPageType);
        } else if (p100AppBanner === 'big') {
            dispatch(hideOverlay(OverlayId.APP_PUSH_BIG));
            gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.BigPushBannerCloseBackground, currentPageType);
        }
        setDismissed();
    };

    const close = () => {
        launchDarklyTracker.trackMetric('bella-app-banner-push-dismiss');
        if (p100AppBanner === 'smart') {
            gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.SmartBannerClose, currentPageType);
        } else if (p100AppBanner === 'small') {
            dispatch(hideOverlay(OverlayId.APP_PUSH_SMALL));
            gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.SmallPushBannerClose, currentPageType);
        } else if (p100AppBanner === 'big') {
            dispatch(hideOverlay(OverlayId.APP_PUSH_BIG));
            gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.BigPushBannerClose, currentPageType);
        }
        setDismissed();
    };

    const openInApp = () => {
        // This would lead away from web to app so tracking request should be sent immediately.
        launchDarklyTracker.trackImportantMetric('bella-app-banner-push-click');
        if (p100AppBanner === 'smart') {
            gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.SmartBannerClick, currentPageType);
        } else if (p100AppBanner === 'small') {
            gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.SmallPushBannerClick, currentPageType);
        } else if (p100AppBanner === 'big') {
            gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.BigPushBannerClick, currentPageType);
        }
        bellaPush(smartAppUrl(host, trackers, getByUserAgent()));
    };

    useEffect(() => {
        if (!isTablet() && shouldShow) {
            launchDarklyTracker.trackVariation('p100AppBanner', p100AppBanner);
            if (p100AppBanner === 'smart') {
                gaTracking.oneHeader.trackAppBannerImpression(
                    TrackAppBannerActions.SmartBannerImpression,
                    currentPageType
                );
            } else if (p100AppBanner === 'small' && !isDoiOverlayActive) {
                dispatch(showOverlay(OverlayId.APP_PUSH_SMALL));
                gaTracking.oneHeader.trackAppBannerImpression(
                    TrackAppBannerActions.SmallPushBannerImpression,
                    currentPageType
                );
            } else if (p100AppBanner === 'big' && !isDoiOverlayActive) {
                dispatch(showOverlay(OverlayId.APP_PUSH_BIG));
                gaTracking.oneHeader.trackAppBannerImpression(
                    TrackAppBannerActions.BigPushBannerImpression,
                    currentPageType
                );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldShow]);

    return {
        close,
        openInApp,
        onBackdropDismiss,
    };
};
